'use client'

import { useEffect, useState } from 'react'

/**
 * Decide si la barra flotante de compra debe estar visible.
 *
 * Criterio: la barra aparece **siempre que el CTA de la ficha no esté en
 * pantalla**, tanto si el usuario todavía no ha llegado a él (cabecera alta, el
 * botón cae por debajo del fold) como si ya lo ha dejado atrás. Antes se
 * comparaba contra el hero entero, así que en el primer caso —justo cuando más
 * falta hace— la barra no se mostraba.
 *
 * «En pantalla» se mide contra el hueco realmente visible: por arriba, el borde
 * inferior del `<header>` sticky (que tapa contenido aunque este siga
 * intersectando el viewport); por abajo, el alto de la ventana.
 *
 * Se descarta `IntersectionObserver` por el mismo motivo que documenta
 * `Header.tsx` para `scrolledPast`: el cruce solo dispara al entrar y al salir,
 * y el valor se queda pegado en escenarios de rebote — que aquí son la norma,
 * porque el CTA puede salir y volver a entrar por ambos bordes.
 */
export function useBuyBoxVisibility(ctaId: string): boolean {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    let frame = 0

    const measure = () => {
      frame = 0

      // Cierre de página: oculta la barra del todo en vez de ir esquivando el
      // footer — no aporta nada seguir insistiendo con el CTA justo cuando el
      // usuario ya ha llegado al final del contenido.
      //
      // El ÚLTIMO `<footer>` del documento, no el primero: `SideSheet` también
      // renderiza uno y, con un side sheet abierto, queda antes en el DOM.
      const footers = document.querySelectorAll('footer')
      const footer = footers[footers.length - 1]
      const reachedEnd = footer ? footer.getBoundingClientRect().top <= window.innerHeight : false

      const cta = document.getElementById(ctaId)
      const header = document.querySelector('header')
      const coveredTop = header?.getBoundingClientRect().bottom ?? 0

      // Sin ancla (ficha sin CTA en cabecera) se asume que no hay nada visible
      // que duplicar, así que la barra se muestra.
      const rect = cta?.getBoundingClientRect()
      const ctaOnScreen = rect ? rect.bottom > coveredTop && rect.top < window.innerHeight : false

      setVisible(!ctaOnScreen && !reachedEnd)
    }

    // El `scroll` puede dispararse muchas veces por frame y `measure` hace
    // varias lecturas de layout: se agrupan en un único `requestAnimationFrame`.
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [ctaId])

  return visible
}
